import {
    ConfigurableProduct,
    GroupedProduct,
    ProductInterface,
    SimpleProduct
} from '@pylot-data/fwrdschema'
import {
    formatPrice,
    getStringAttributeValue,
    getTrimmedUrl
} from '../../../util'
import { buildModelUrl, getFirstNotEmpty } from '@corratech/pylot-utils'
import { getPriceValidUntil } from './getPriceValidUntil'
import { getItemCondition } from './getItemCondition'
import { getProductAvailability } from './getProductAvailability'
import { PylotFrontendConfig, ModelTypeEnum } from 'config'

const PRODUCT_TYPE_OFFER = ['SimpleProduct']

const isSimpleOfferProduct = (
    product: SimpleProduct | ConfigurableProduct | GroupedProduct
): product is SimpleProduct => {
    if (
        product.__typename &&
        PRODUCT_TYPE_OFFER.includes(product?.__typename)
    ) {
        return true
    }
    return !Object.keys(product).includes('variants')
}

type offerElement = {
    '@type': string
    priceCurrency: string
    price: string
    priceValidUntil?: string
    availability: string
    itemCondition: string
    sku: string
    name: string
    image: string
}

type OfferData = {
    '@type'?: string
    url?: string
    priceCurrency?: string
    price?: string | number
    priceValidUntil?: string
    offerCount?: string | number
    highPrice?: string | number
    lowPrice?: string | number
    gtin12?: string
    itemCondition?: string
    availability?: string
    offers?: Array<offerElement>
    shippingDetails?: any
}

export const getOfferData = (
    product: ProductInterface,
    name: string,
    description: string,
    config: PylotFrontendConfig,
    getShippingDetails?: () => any
): Record<string, OfferData> => {
    const offerData = isSimpleOfferProduct(product)
        ? getSimpleOffer(product, config)
        : getAggregateOffer(product, name, config)

    if (getShippingDetails) offerData.shippingDetails = getShippingDetails()

    return offerData ? { offers: offerData } : {}
}

const getSimpleOffer = (
    product: ProductInterface,
    config: PylotFrontendConfig
): OfferData => {
    const price = formatPrice(
        getStringAttributeValue(product, 'price_amount', config),
        config
    )
    const priceCurrency = getStringAttributeValue(
        product,
        'price_currency',
        config
    )
    return {
        '@type': 'Offer',
        url: buildModelUrl(
            ModelTypeEnum.PRODUCT,
            String(product.url_key),
            config
        ),
        priceCurrency: priceCurrency,
        price: price,
        priceValidUntil: getPriceValidUntil(config),
        itemCondition: getItemCondition(product, config),
        availability: getProductAvailability(product)
    }
}

const getChildItems = (product: GroupedProduct | ConfigurableProduct) => {
    if (product.__typename === 'GroupedProduct') {
        return product.items?.map((item) => item?.product) || []
    }
    if (product.__typename === 'ConfigurableProduct') {
        return product.variants?.map((item) => item?.product) || []
    }
    return []
}

const getAggregateOffer = (
    product: ProductInterface,
    parentName: string,
    config: PylotFrontendConfig
): OfferData => {
    let lowPrice = 0,
        highPrice = 0,
        priceCurrency: string | null = null
    const {
        seo: { product: productConfig }
    } = config

    const offers: Array<offerElement> = []
    getChildItems(product).forEach((item) => {
        if (!item) {
            return
        }

        priceCurrency = getFirstNotEmpty([
            getStringAttributeValue(item, 'price_currency', config),
            productConfig?.price?.currency
        ])
        const price = Number(
            getStringAttributeValue(item, 'price_amount', config)
        )

        if (price && (!lowPrice || !highPrice)) {
            lowPrice = price
            highPrice = price
        } else if (price && lowPrice && highPrice) {
            lowPrice = price <= lowPrice ? price : lowPrice
            highPrice = price >= highPrice ? price : highPrice
        }

        const itemName = getStringAttributeValue(item, 'name', config)

        const imageUrl = getTrimmedUrl(item?.media_gallery?.[0]?.url || '')

        offers.push({
            '@type': 'Offer',
            priceCurrency: String(priceCurrency),
            price: formatPrice(price, config),
            priceValidUntil: getPriceValidUntil(config),
            availability: getProductAvailability(item),
            itemCondition: String(getItemCondition(item, config)),
            name: itemName !== '' ? itemName : parentName,
            sku: String(item.sku),
            image: imageUrl
        })
    })

    return {
        '@type': 'AggregateOffer',
        url: buildModelUrl(
            ModelTypeEnum.PRODUCT,
            String(product.url_key),
            config
        ),
        priceCurrency: String(priceCurrency),
        offerCount: String(offers.length),
        highPrice: formatPrice(highPrice, config),
        lowPrice: formatPrice(lowPrice, config),
        offers: offers
    }
}
